import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchWithSecrets } from "../../utils/addressSecrets";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  KNOWN_PASSWORD_LEVELS,
  usePasswordResetContext,
} from "../../hooks/usePasswordResetContext";
import EndUserPublicLayout from "../../components/atoms/EndUserPublicLayout";
import ResetPasswordForm from "../../components/molecules/ResetPasswordForm";

const UserResetPassword = () => {
  const { t } = useTranslation("clientView");
  const navigate = useNavigate();
  const searchParams = useSearchWithSecrets();

  const userId = searchParams.get("user_identification");
  const entityId = searchParams.get("entity_identification");
  const token = searchParams.get("token");

  const hasRequiredParams = !!userId && !!entityId && !!token;

  useEffect(() => {
    if (!hasRequiredParams) {
      navigate("/link-unavailable?flow=password-reset", { replace: true });
    }
  }, [navigate, hasRequiredParams]);

  const [showResult, setShowResult] = useState(false);

  const {
    entityLogo,
    entityName,
    passwordLevel,
    passwordRules,
    acceptedLevels,
  } = usePasswordResetContext(userId, entityId, token, {
    redirectOnError: "/link-unavailable?flow=password-reset",
  });

  const levelHint =
    passwordLevel && KNOWN_PASSWORD_LEVELS.has(passwordLevel)
      ? (t(`passwordLevels.${passwordLevel}`) as string)
      : null;

  if (!hasRequiredParams || !userId || !entityId || !token) return null;

  return (
    <>
      <Helmet>
        <title>Super User Manager - Reset password</title>
        <meta
          name="description"
          content="Super User Manager - Reset your password"
        />
        <meta name="robots" content="noindex" />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/user/reset-password`}
        />
      </Helmet>

      <EndUserPublicLayout
        entityLogo={entityLogo}
        entityName={entityName}
        title={!showResult ? (t("resetPassword.title") as string) : undefined}
      >
        {showResult ? (
          <div className="flex flex-col items-center justify-center py-6 gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div className="text-gray-900 text-lg font-semibold font-figtree text-center">
              {t("resetPassword.msg")}
            </div>
          </div>
        ) : (
          <ResetPasswordForm
            entityId={entityId}
            userId={userId}
            token={token}
            passwordLevel={passwordLevel}
            passwordRules={passwordRules}
            acceptedLevels={acceptedLevels}
            levelHint={levelHint}
            labels={{
              newPasswordLabel: t("resetPassword.label_1") as string,
              newPasswordPlaceholder: t("resetPassword.label_1") as string,
              confirmPasswordLabel: t("resetPassword.label_2") as string,
              confirmPasswordPlaceholder: t("resetPassword.label_2") as string,
              submitLabel: t("resetPassword.resetBtn") as string,
              matchLabel: t("resetPassword.passwordsMatch") as string,
              passwordMismatchError: t(
                "resetPassword.passwordsDoNotMatch"
              ) as string,
            }}
            onSuccess={() => setShowResult(true)}
          />
        )}
      </EndUserPublicLayout>
    </>
  );
};

export default UserResetPassword;
